/* global googletag, gptParentSect, gptSub, gptSect, gptContentId, gptKeyword, gptUrl */

// Import settings and configurations
import {
  googleAdsSettings,
  adSlotArr,
  immediateLoadSlots,
  refreshConfig,
  initialRefreshTracker,
} from './gpt-settings';

// Ensure the googletag object is available globally
window.googletag = window.googletag || { cmd: [] };

// Define Google Ad details
const gptNetwork = googleAdsSettings?.gpt_site_id;
const intertialsLogo = [
  `<img alt="${googleAdsSettings?.gpt_interstitial_logo_alt}" src="${googleAdsSettings?.gpt_interstitial_logo_src}">`,
  googleAdsSettings?.gpt_interstitial_header_colour_bg,
];

// Define the empty ad slots set
const definedSlots = new Set();

// Update the ad related CSS custom properties
document.documentElement.style.setProperty(
  '--interstitial-header-bg',
  googleAdsSettings?.gpt_interstitial_header_colour_bg,
);
document.documentElement.style.setProperty(
  '--interstitial-header-text',
  googleAdsSettings?.gpt_interstitial_header_colour_text,
);

// Initialize tracking objects with initial states
const refreshTracker = { ...initialRefreshTracker };

/**
 * Debug state for ad visibility
 */
const debugState = {
  enabled: false,
};

/**
 * Debounce helper function to limit the rate at which a function can fire.
 *
 * @param {Function} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay
 *
 * @returns {Function} A debounced version of the original function
 */
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    /**
     * Inner debounce closure
     */
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Closes the interstitial overlay ad.
 *
 * @param {HTMLElement} interstitialSlot - The DOM element containing the interstitial ad
 *
 * @returns {void}
 */
const bannerCloseSkipAd = (interstitialSlot) => {
  interstitialSlot.classList.remove('active');
};

/**
 * Sets up the interstitial overlay with countdown timer and close functionality.
 *
 * @param {HTMLElement} interstitialSlot - The DOM element to contain the interstitial
 * @param {HTMLIFrameElement} iframe - The iframe containing the ad content
 *
 * @returns {void}
 */
const constructInterstitialPopup = (interstitialSlot, iframe) => {
  // If either property is missing, return
  if (!interstitialSlot || !iframe) {
    return;
  }

  iframe.addEventListener('load', () => {
    // Define the countdown timer
    let sec = 5;

    const [logo] = intertialsLogo;

    // Create the interstitialHeader elements
    const interstitialHeader = document.createElement('div');
    interstitialHeader.classList.add('intertials-header');

    const logoWrap = document.createElement('div');
    logoWrap.classList.add('logo-wrap');
    logoWrap.innerHTML = logo;

    const continueButton = document.createElement('button');
    continueButton.classList.add('continue-button');
    continueButton.setAttribute('disabled', '');
    continueButton.innerHTML = `You can skip this ad in ${sec}`;

    // Append the elements to the DOM
    interstitialHeader.appendChild(logoWrap);
    interstitialHeader.appendChild(continueButton);
    interstitialSlot.prepend(interstitialHeader);

    // Add the active class
    interstitialSlot.classList.add('active');

    // Set the interval to count down
    const interval = setInterval(() => {
      sec -= 1;
      if (sec > 0) {
        continueButton.innerHTML = `You can skip this ad in ${sec}`;
      } else if (sec === 0) {
        continueButton.innerHTML = `Continue to Site »`;
        continueButton.removeAttribute('disabled');
        continueButton.addEventListener('click', () => {
          bannerCloseSkipAd(interstitialSlot);
        });
      } else if (sec === -10) {
        bannerCloseSkipAd(interstitialSlot);
        clearInterval(interval);
      }
    }, 1000);
  });
};

/**
 * Generates GPT size mapping for responsive ad slots.
 *
 * @param {Object} adArr - The ad configuration object
 * @param {Array<Array<number>>} adArr.primarySlot - Primary ad sizes
 * @param {Array<Array<number>>} adArr.mobSlot - Mobile ad sizes
 *
 * @returns {googletag.SizeMapping} A built GPT size mapping object
 */
const getGPTSizeMapping = (adArr) =>
  googletag
    .sizeMapping()
    .addSize([992, 0], adArr.primarySlot)
    .addSize([200, 0], adArr.mobSlot)
    .addSize([0, 0], adArr.primarySlot)
    .build();

/**
 * Helper function to check if an ad type is in the refreshable configuration
 *
 * @param {string} adType - The ad type to check
 *
 * @returns {boolean} Whether the ad type is configured as refreshable
 */
const isRefreshableAdType = (adType) =>
  refreshConfig.refreshableAdTypes.some((config) => config.name === adType);

/**
 * Sets up event listeners for an ad slot
 *
 * @param {googletag.Slot} slot - The GPT slot object
 * @param {string} slotName - The name of the slot
 * @param {HTMLElement} googleAd - The DOM element for the ad
 * @param {string} gptAdtype - The type of ad
 */
const setupAdEventListeners = (slot, slotName, googleAd, gptAdtype) => {
  let overlayCreated = false;

  // Track which slots have had listeners attached
  const slotsWithListeners = new Set();

  // Only set up listeners if they haven't been set up for this slot before
  if (!slotsWithListeners.has(slotName)) {
    // googletag.pubads().addEventListener('slotRequested', (event) => {
    //   if (event.slot === slot) {
    //     console.log(`slotRequested: ${slotName}`);
    //   }
    // });

    // googletag.pubads().addEventListener('slotResponseReceived', (event) => {
    //   if (event.slot === slot) {
    //     console.log(`slotResponseReceived: ${slotName}`);
    //   }
    // });

    // googletag.pubads().addEventListener('slotVisibilityChanged', (event) => {
    //   if (event.slot === slot) {
    //     console.log(`slotVisibilityChanged: ${slotName}`);
    //     console.log(`Visible area: ${event.inViewPercentage}%`);
    //   }
    // });

    // googletag.pubads().addEventListener('slotOnload', (event) => {
    //   if (event.slot === slot) {
    //     console.log(`slotOnload: ${slotName}`);
    //   }
    // });

    // Once the ad slot render has ended
    googletag.pubads().addEventListener('slotRenderEnded', (event) => {
      if (event.slot === slot) {
        if (event.isEmpty) {
          // eslint-disable-next-line no-console
          console.debug(`slotRenderEnded: ${slotName} empty`);
        } else {
          // Add rendered class to prevent CLS
          googleAd.classList.add('gpt-slot-rendered');
          // console.log(`slotRenderEnded: ${slotName}`);
          // log the size of the rendered ad
          // console.log(`Size of rendered ad: ${googleAd.offsetWidth}x${googleAd.offsetHeight}`);
        }

        // If the pullup billboard ad exists, remove the class after X seconds
        if (
          !event.isEmpty &&
          googleAd.getAttribute('data-adtype') === 'banner970x250' &&
          document.body.classList.contains('has-pullup-ad-billboard')
        ) {
          setTimeout(() => {
            document.body.classList.remove('has-pullup-ad-billboard');
          }, parseInt(googleAdsSettings?.gpt_billboard_pullup_timeout, 10) || 5000);
        }

        // If the interstitial ad exists, construct the popup
        if (!event.isEmpty && googleAd.getAttribute('data-adtype') === 'banner750x450') {
          if (!overlayCreated) {
            const interstitialSlotFrame = googleAd.querySelector('iframe');
            constructInterstitialPopup(googleAd, interstitialSlotFrame);
            overlayCreated = true;
          }
        }
      }
    });

    googletag.pubads().addEventListener('impressionViewable', (event) => {
      if (event.slot === slot) {
        // Only track viewability for refreshable ad types
        if (refreshConfig.refreshEnabled && isRefreshableAdType(gptAdtype)) {
          // Update the last refresh time when the ad becomes viewable
          const slotInfo = refreshTracker.refreshableSlots.get(slotName);
          if (slotInfo) {
            refreshTracker.refreshableSlots.set(slotName, {
              ...slotInfo,
              lastRefreshTime: Date.now(),
            });
          }
        }
      }
    });

    // Mark this slot as having listeners attached
    slotsWithListeners.add(slotName);
  }
};

/**
 * Displays a specific ad slot.
 *
 * @param {string} slotId - The ID of the slot to display
 *
 * @returns {void}
 */
const displayAdSlot = (slotId) => {
  if (!slotId) return;

  googletag.cmd.push(() => {
    googletag.display(slotId);
  });
};

/**
 * Defines ad slots for dynamically injected content.
 *
 * @param {number|string} slotIndex - The index or identifier for the ad slot
 * @param {string} [uniqueId=''] - Optional unique identifier for the ad slot
 * @param {HTMLElement} [context] - Optional DOM context to search for ad slots
 *
 * @returns {void}
 */
const defineInjectedAdSlots = (slotIndex, uniqueId = '', context) => {
  let googleAdsList = [];
  if (context) {
    googleAdsList = Array.from(context.querySelectorAll('.googleAds'));
  } else {
    googleAdsList = Array.from(document.querySelectorAll('.googleAds')).filter(
      (googleAd) => !definedSlots.has(googleAd.id),
    );
  }

  // Define all slots first
  const newlyDefinedSlots = [];

  googleAdsList.forEach((googleAd) => {
    if (googleAd.children.length === 0 || googleAd.children.length === 1) {
      const gptAdtype = googleAd.dataset.adtype;
      adSlotArr.forEach((adSlot) => {
        if (adSlot.name === gptAdtype) {
          const currentSlotIndex = googleAd.dataset.index;
          const network = `/${gptNetwork}`;
          const slotName = `ad-slot-${currentSlotIndex}${uniqueId}`;

          // Skip if already defined
          if (definedSlots.has(googleAd.id)) {
            return;
          }

          // Set up the ad element
          // eslint-disable-next-line no-param-reassign
          googleAd.id = slotName;
          googleAd.setAttribute('data-index', `${currentSlotIndex}${uniqueId}`);

          // Define the slot
          googletag.cmd.push(() => {
            const slot = googletag
              .defineSlot(network, adSlot.primarySlot, slotName)
              .defineSizeMapping(getGPTSizeMapping(adSlot))
              .setCollapseEmptyDiv(true, true)
              .addService(googletag.pubads());

            // Enable SRA (Single Request Architecture)
            if (googleAdsSettings?.gpt_enable_sra) {
              googletag.pubads().enableSingleRequest();
            }

            // Enable lazy loading by default
            if (googleAdsSettings?.gpt_enable_native_lazy_loading) {
              googletag.pubads().enableLazyLoad({
                fetchMarginPercent: parseInt(googleAdsSettings?.gpt_lazy_fetchmargin, 10) || 100, // Fetch ads when they come within X of the viewport height
                renderMarginPercent: parseInt(googleAdsSettings?.gpt_lazy_rendermargin, 10) || 0, // Render ads when they come within X of the viewport height
                mobileScaling: parseFloat(googleAdsSettings?.gpt_lazy_mobilescaling) || 2.0, // Scale up the fetch margin on mobile devices
              });
            }

            // Set up event listeners
            setupAdEventListeners(slot, slotName, googleAd, gptAdtype);

            // Mark as defined
            definedSlots.add(googleAd.id);
            newlyDefinedSlots.push(googleAd);
          });
        }
      });
    }
  });

  // Enable services after defining all slots
  googletag.cmd.push(() => {
    googletag.enableServices();

    // Display all newly defined slots
    newlyDefinedSlots.forEach((ad) => {
      displayAdSlot(ad.id);
    });
  });
};

/**
 * Observes DOM mutations for dynamically added content and initializes ads.
 * Uses debouncing to prevent excessive processing of mutations.
 *
 * @returns {void}
 */
const observeDynamicAds = () => {
  const observer = new MutationObserver(
    debounce((mutations) => {
      mutations.forEach((mutation) => {
        const addedPosts = Array.from(mutation.addedNodes).filter((node) =>
          node.classList?.contains('type-post'),
        );

        if (addedPosts.length) {
          addedPosts.forEach((node) => {
            const uniqueId = `-${Math.floor(Math.random() * 10000)}`;
            defineInjectedAdSlots(0, uniqueId, node);
          });
        }
      });
    }, 500),
  );

  const contentContainer = document.querySelector('.content-area, #main-content');
  if (contentContainer) {
    observer.observe(contentContainer, {
      childList: true,
      subtree: true,
    });

    // Cleanup observer on page unload
    window.addEventListener('pagehide', () => observer.disconnect());
  }
};

/**
 * Manages the refresh cycle for eligible ad slots
 */
const manageAdRefresh = () => {
  if (refreshTracker.refreshTimer) {
    clearInterval(refreshTracker.refreshTimer);
  }

  // Check ad visibility at refreshTimerInterval to get close to the refreshInterval setting
  refreshTracker.refreshTimer = setInterval(() => {
    const currentTime = Date.now();
    const slotsToRefresh = [];

    // For each ad slot that should be refreshed
    refreshTracker.refreshableSlots.forEach((slotInfo, slotId) => {
      const currentCount = refreshTracker.refreshCounts.get(slotId) || 0;
      const isVisible = refreshTracker.visibilityState.get(slotId);
      const timeSinceLastRefresh = currentTime - slotInfo.lastRefreshTime;

      // Check eligibility conditions
      const hasReachedMaxRefreshes = currentCount >= refreshConfig.maxRefreshes;
      const hasEnoughTimePassed = timeSinceLastRefresh >= refreshConfig.refreshInterval;

      // Only process if all conditions are met
      if (!hasReachedMaxRefreshes && isVisible && hasEnoughTimePassed) {
        // Add refreshed class to prevent CLS during refresh
        const adContainer = document.getElementById(slotId);
        if (adContainer) {
          adContainer.classList.add('gpt-slot-refreshed');
        }

        // Add slot to refresh list
        slotsToRefresh.push(slotInfo.slot);

        // Update tracking info
        refreshTracker.refreshCounts.set(slotId, currentCount + 1);
        refreshTracker.refreshableSlots.set(slotId, {
          ...slotInfo,
          lastRefreshTime: currentTime,
        });
      } else if (hasReachedMaxRefreshes) {
        // console.log(`Slot ${slotId} has reached max refreshes (${refreshConfig.maxRefreshes})`);
      }
    });

    // Batch refresh all eligible slots
    if (slotsToRefresh.length > 0) {
      googletag.cmd.push(() => {
        googletag.pubads().refresh(slotsToRefresh);
      });
    }
  }, refreshConfig.refreshTimerInterval);
};

/**
 * Creates and manages a global indicator panel for ad slot visibility
 */
const createGlobalIndicatorPanel = () => {
  if (!debugState.enabled) return {};

  // Remove existing panel if any
  const existingPanel = document.getElementById('global-ad-indicator');
  existingPanel?.remove();

  // Create the panel container
  const panel = document.createElement('div');
  panel.id = 'global-ad-indicator';

  // Add a header with toggle button
  const header = document.createElement('div');
  header.id = 'global-ad-header';

  const title = document.createElement('span');
  title.textContent = 'Ad Slot Visibility Status';
  header.appendChild(title);

  const closeButton = document.createElement('button');
  closeButton.id = 'global-ad-close';
  closeButton.textContent = '×';

  /**
   * Run the toggle function to close the panel
   */
  closeButton.onclick = () => window.toggleAdVisibilityDebug();
  header.appendChild(closeButton);

  panel.appendChild(header);

  if (refreshConfig.refreshEnabled) {
    // Create container for slot indicators
    const slotsContainer = document.createElement('div');
    slotsContainer.id = 'global-slots-container';
    panel.appendChild(slotsContainer);
  } else {
    // Create container for disabled refreshable ads message
    const noRefreshContainer = document.createElement('div');
    noRefreshContainer.id = 'global-no-refresh-container';
    noRefreshContainer.textContent = 'Refreshable ads are currently disabled';
    panel.appendChild(noRefreshContainer);
  }

  document.body.appendChild(panel);
  return panel;
};

/**
 * Initializes GPT with targeting parameters and global settings.
 * Should be called only once at the start of ad loading.
 *
 * @returns {void}
 */
const initializeGPT = () => {
  googletag.cmd.push(() => {
    // Set targeting parameters
    googletag
      .pubads()
      .setTargeting('parent', gptParentSect)
      .setTargeting('sub', gptSub)
      .setTargeting('sect', gptSect)
      .setTargeting('artid', gptContentId)
      .setTargeting('cat', gptKeyword)
      .setTargeting('url', gptUrl);

    // Enable SRA (Single Request Architecture) if configured
    if (googleAdsSettings?.gpt_enable_sra) {
      googletag.pubads().enableSingleRequest();
    }

    // Enable lazy loading by default if configured
    if (googleAdsSettings?.gpt_enable_native_lazy_loading) {
      googletag.pubads().enableLazyLoad({
        fetchMarginPercent: parseInt(googleAdsSettings?.gpt_lazy_fetchmargin, 10) || 100,
        renderMarginPercent: parseInt(googleAdsSettings?.gpt_lazy_rendermargin, 10) || 0,
        mobileScaling: parseFloat(googleAdsSettings?.gpt_lazy_mobilescaling) || 2.0,
      });
    }
  });
};

/**
 * Checks if an ad type is eligible for refresh based on current conditions
 *
 * @param {string} adType - The type of ad to check
 * @returns {boolean} Whether the ad type is eligible for refresh
 */
const isAdTypeRefreshable = (adType) => {
  const adConfig = refreshConfig.refreshableAdTypes.find((config) => config.name === adType);
  if (!adConfig) return false;

  // Check viewport width restriction
  const viewportWidth = window.innerWidth;
  if (adConfig.minViewport > 0 && viewportWidth < adConfig.minViewport) {
    return false;
  }

  // Check template restrictions
  if (adConfig.allowedTemplates.length > 0) {
    const bodyClasses = document.body.classList;
    const isAllowedTemplate = adConfig.allowedTemplates.some((template) =>
      bodyClasses.contains(template),
    );
    if (!isAllowedTemplate) {
      return false;
    }
  }

  return true;
};

/**
 * Removes all debug elements from the page
 *
 * @returns {void}
 */
const removeDebugElements = () => {
  // Remove global indicator panel
  const panel = document.getElementById('global-ad-indicator');
  panel?.remove();
};

/**
 * Toggles the ad visibility debug mode
 *
 * @returns {void}
 */
window.toggleAdVisibilityDebug = () => {
  debugState.enabled = !debugState.enabled;

  if (debugState.enabled) {
    // eslint-disable-next-line no-console
    console.warn('Ad visibility debug mode enabled');
    createGlobalIndicatorPanel();
  } else {
    // eslint-disable-next-line no-console
    console.warn('Ad visibility debug mode disabled');
    removeDebugElements();
  }
};

/**
 * Updates the global indicator panel with current slot statuses
 */
const updateGlobalIndicatorPanel = () => {
  // Return if debug is disabled
  if (!debugState.enabled) return;

  // Return if the debug container doesn't exist
  const container = document.getElementById('global-slots-container');
  if (!container) return;

  container.innerHTML = '';

  // Sort slots by ID for consistent display
  const sortedSlots = Array.from(refreshTracker.slotTypes.entries()).sort();

  sortedSlots.forEach(([slotId, adType]) => {
    if (isRefreshableAdType(adType)) {
      const isVisible = refreshTracker.visibilityState.get(slotId) || false;
      const refreshCount = refreshTracker.refreshCounts.get(slotId) || 0;
      const slotInfo = refreshTracker.refreshableSlots.get(slotId);
      const adConfig = refreshConfig.refreshableAdTypes.find((config) => config.name === adType);
      const isCurrentlyEligible = isAdTypeRefreshable(adType);

      const slotElement = document.createElement('div');
      slotElement.classList.add('global-ad-slot');

      /**
       * Helper function to determine background color
       *
       * @returns {string} The background colour
       */
      const getBackgroundColor = () => {
        if (!isCurrentlyEligible) {
          return 'rgba(128, 128, 128, 0.2)';
        }
        return isVisible ? 'rgba(0, 255, 0, 0.2)' : 'rgba(255, 0, 0, 0.2)';
      };

      /**
       * Helper function to determine status text
       *
       * @returns {string} The status text
       */
      const getStatusText = () => {
        if (!isCurrentlyEligible) {
          return 'NOT ELIGIBLE';
        }
        return isVisible ? 'VISIBLE' : 'NOT VISIBLE';
      };

      slotElement.style.cssText = `background: ${getBackgroundColor()};`;

      const timeSinceRefresh = slotInfo
        ? Math.floor((Date.now() - slotInfo.lastRefreshTime) / 1000)
        : 0;

      const templateRestrictions =
        adConfig.allowedTemplates.length > 0
          ? `Template: ${adConfig.allowedTemplates.join(', ')}<br>`
          : '';

      slotElement.innerHTML = `
        ${slotId} (${adType})<br>
        Status: ${getStatusText()}<br>
        Refreshes: ${refreshCount}/${refreshConfig.maxRefreshes}<br>
        Min Viewport: ${adConfig.minViewport}px (current: ${window.innerWidth}px)<br>
        ${templateRestrictions}
        Time since last: ${timeSinceRefresh}s
      `;

      container.appendChild(slotElement);
    }
  });
};

/**
 * Defines all ad slots on the page without displaying them.
 * This allows for better control over when ads are displayed.
 *
 * @returns {Map<string, googletag.Slot>} A map of slot IDs to their GPT slot objects
 */
const defineAllAdSlots = () => {
  const definedSlotsMap = new Map();

  // Process all ad slots on the page
  document.querySelectorAll('.googleAds').forEach((googleAd) => {
    try {
      const slotIndex = googleAd.dataset.index;
      const gptAdtype = googleAd.dataset.adtype;
      const adSlot = adSlotArr.find((slot) => slot.name === gptAdtype);

      if (!adSlot || !slotIndex || !gptAdtype) {
        // eslint-disable-next-line no-console
        console.warn('Invalid ad configuration:', { slotIndex, gptAdtype });
        return;
      }

      const network = `/${gptNetwork}`;
      const slotName = `ad-slot-${slotIndex}`;

      // Skip if already defined
      if (definedSlots.has(googleAd.id)) {
        return;
      }

      googletag.cmd.push(() => {
        const slot = googletag
          .defineSlot(network, adSlot.primarySlot, slotName)
          .defineSizeMapping(getGPTSizeMapping(adSlot))
          .setCollapseEmptyDiv(true, true)
          .addService(googletag.pubads());

        // console.log(`defineSlot: ${slotName}`);

        // Apply lazy loading settings based on ad type
        const skipLazyLoad = immediateLoadSlots.includes(gptAdtype);
        if (!skipLazyLoad) {
          slot.setTargeting('loading', 'lazy');
        }

        // Initialize refresh tracking for refreshable ads
        if (
          refreshConfig.refreshEnabled &&
          isRefreshableAdType(gptAdtype) &&
          isAdTypeRefreshable(gptAdtype)
        ) {
          refreshTracker.slotTypes.set(slotName, gptAdtype);
          refreshTracker.refreshableSlots.set(slotName, {
            slot,
            lastRefreshTime: Date.now(),
            readyToRefresh: false,
          });
          refreshTracker.refreshCounts.set(slotName, 0);
          refreshTracker.visibilityState.set(slotName, false);

          // Start refresh management if this is the first refreshable slot
          if (refreshTracker.refreshableSlots.size === 1) {
            manageAdRefresh();
          }
        }

        // Set up event listeners
        setupAdEventListeners(slot, slotName, googleAd, gptAdtype);

        // Store the slot in our map
        definedSlotsMap.set(slotName, slot);

        // Mark as defined
        definedSlots.add(googleAd.id);
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error defining ad slot:', error);
    }
  });

  return definedSlotsMap;
};

/**
 * Sets up visibility tracking for ad refreshing
 *
 * @param {Array<HTMLElement>} adElements - Array of ad elements to track
 */
const setupRefreshVisibilityTracking = (adElements) => {
  if (!refreshConfig.refreshEnabled) return;

  // Separate observer for refresh visibility tracking with less frequent updates
  const refreshObserver = new IntersectionObserver(
    (entries) => {
      const updates = new Map();

      entries.forEach((entry) => {
        const adElement = entry.target;
        const slotId = adElement.id;
        const adType = adElement.dataset.adtype;
        const previousVisibility = refreshTracker.visibilityState.get(slotId);
        const currentVisibility = entry.intersectionRatio >= 0.5;

        // First check if it's a refreshable type at all
        if (!isRefreshableAdType(adType)) {
          return;
        }

        // Then check if it meets current conditions
        const isCurrentlyEligible = isAdTypeRefreshable(adType);

        // If not eligible, mark as not visible
        if (!isCurrentlyEligible) {
          if (previousVisibility !== false) {
            updates.set(slotId, false);
          }
          return;
        }

        // Only update visibility if the ad is currently eligible
        if (isCurrentlyEligible && previousVisibility !== currentVisibility) {
          updates.set(slotId, currentVisibility);
        }
      });

      // Batch visibility updates
      if (updates.size > 0) {
        updates.forEach((isVisible, slotId) => {
          refreshTracker.visibilityState.set(slotId, isVisible);
        });

        // Update the global indicator panel
        updateGlobalIndicatorPanel();
      }
    },
    {
      rootMargin: '0px',
      threshold: [0.25, 0.5, 0.75, 1.0],
    },
  );

  // Apply refresh observer to all ads
  adElements.forEach((ad) => {
    const adType = ad.dataset.adtype;
    if (isRefreshableAdType(adType)) {
      refreshObserver.observe(ad);
    }
  });

  // Cleanup observer on page unload
  window.addEventListener('pagehide', () => {
    refreshObserver.disconnect();
  });
};

/**
 * Populate any ad slots on the page.
 * When enabled, custom lazy loading will be used.
 *
 * @returns {void}
 */
const populateAdSlots = () => {
  //
  const useCustomLazyLoading = googleAdsSettings?.gpt_enable_custom_lazy_loading;

  // Define all ad slots first
  defineAllAdSlots();

  // Enable services after all slots are defined
  googletag.cmd.push(() => {
    googletag.enableServices();
  });

  // Create separate arrays for immediate and lazy load ads
  const immediateAds = [];
  const lazyAds = [];

  // Sort ads into appropriate arrays
  document.querySelectorAll('.googleAds').forEach((ad) => {
    if (useCustomLazyLoading) {
      // Separate ads if custom lazy loading is enabled
      const adType = ad.dataset.adtype;
      if (immediateLoadSlots.includes(adType)) {
        immediateAds.push(ad);
      } else {
        lazyAds.push(ad);
      }
    } else {
      // Push all ads to load immediately when custom lazy loading is disabled
      immediateAds.push(ad);
    }
  });

  // Immediately display priority ads
  immediateAds.forEach((ad) => {
    displayAdSlot(ad.id);
  });

  // Use IntersectionObserver to display lazy ads when they come into view
  const lazyLoadObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          displayAdSlot(entry.target.id);
          lazyLoadObserver.unobserve(entry.target); // Stop observing once displayed
        }
      });
    },
    {
      rootMargin: googleAdsSettings?.gpt_lazy_rootmargin ?? '50px 0px',
      threshold: Math.min(Math.max(parseFloat(googleAdsSettings?.gpt_lazy_threshold) || 0.5, 0), 1),
    },
  );

  // Apply observer to lazy ads
  lazyAds.forEach((ad) => {
    lazyLoadObserver.observe(ad);
  });

  // Set up refresh visibility tracking
  setupRefreshVisibilityTracking([...immediateAds, ...lazyAds]);
};

/**
 * Run function on page load
 */
window.addEventListener('load', () => {
  // Initialize GPT
  initializeGPT();

  // Load the ad slots
  populateAdSlots();

  // Set up dynamic ad observation if enabled
  if (googleAdsSettings.google_ads_infinite_scroll_generator) {
    observeDynamicAds();
  }
});

// Update global panel periodically only when debug mode is enabled
setInterval(() => {
  if (debugState.enabled) {
    updateGlobalIndicatorPanel();
  }
}, 1000);

/**
 * Detect viewport changes for ad refreshing
 */
let resizeTimeout;

window.addEventListener('resize', () => {
  if (refreshConfig.refreshEnabled) {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      // Re-evaluate all tracked slots
      refreshTracker.slotTypes.forEach((adType, slotId) => {
        const isEligible = isAdTypeRefreshable(adType);
        const currentVisibility = refreshTracker.visibilityState.get(slotId);

        if (!isEligible && currentVisibility) {
          refreshTracker.visibilityState.set(slotId, false);
          updateGlobalIndicatorPanel();
        }
      });
    }, 250); // Debounce resize events
  }
});
